import type Stripe from 'stripe';
import type {
  FirestoreTimestamp,
  Subscription,
  SubscriptionOrigin,
  SubscriptionStatus,
} from '@/components/subscriptions/types/subscription';
import type { Product } from '@/components/subscriptions/types/product';

/**
 * The states docs/features/260914-settings-subscription-billing/PLAN.md requires the
 * Settings "Subscription & Billing" card to render:
 *   1. active            — Stripe-managed subscription in good standing (Manage in Stripe)
 *   2. admin-granted      — admin-granted access (origin 'local'), no portal, no cancel
 *   3. none               — no subscription document at all
 *   4. expired            — subscription exists but is past_due/canceled/expired/incomplete
 *   5. role-access        — no subscription, but the caller's role grants access on its own
 *                           (administrators only, see {@link buildBillingSummary}); no CTA,
 *                           no portal button, no status badge
 */
export type BillingState = 'active' | 'admin-granted' | 'expired' | 'none' | 'role-access';

export type BillingInterval = 'day' | 'week' | 'month' | 'year';

export interface BillingSummary {
  state: BillingState;
  planName: string | null;
  amount: number | null;
  currency: string | null;
  status: SubscriptionStatus | null;
  currentPeriodEnd: string | null;
  isLifetime: boolean;
  origin: SubscriptionOrigin | null;
  canManageInStripe: boolean;
  /**
   * Not mirrored into Firestore (PLAN.md constraint 4) — always false from
   * {@link buildBillingSummary}. The route overlays a live Stripe read via
   * {@link overlayLiveStripeFields} for `origin: 'stripe'` records.
   */
  cancelAtPeriodEnd: boolean;
  /**
   * The billing interval of the price actually booked (day/week/month/year). Firestore has
   * no price data at all, so this is always null from {@link buildBillingSummary} and only
   * ever set by {@link overlayLiveStripeFields} from a live Stripe read. Null for a lifetime
   * or one-off purchase, or when no live read was possible — never guessed.
   */
  interval: BillingInterval | null;
  /**
   * Der Zugang dieses Kontos haengt an seiner Rolle, nicht an diesem Abo — heute nur bei
   * Administratoren. Steht AUCH dann, wenn ein Abo existiert: dann ist die Karte echt (das
   * Abo kostet Geld und muss verwaltet werden koennen), aber ohne Hinweis liest sie sich,
   * als haenge der Zugang daran.
   */
  accessFromRole: boolean;
}

const NO_SUBSCRIPTION_SUMMARY: BillingSummary = {
  accessFromRole: false,
  state: 'none',
  planName: null,
  amount: null,
  currency: null,
  status: null,
  currentPeriodEnd: null,
  isLifetime: false,
  origin: null,
  canManageInStripe: false,
  cancelAtPeriodEnd: false,
  interval: null,
};

const ROLE_ACCESS_SUMMARY: BillingSummary = {
  accessFromRole: true,
  state: 'role-access',
  planName: null,
  amount: null,
  currency: null,
  status: null,
  currentPeriodEnd: null,
  isLifetime: false,
  origin: null,
  canManageInStripe: false,
  cancelAtPeriodEnd: false,
  interval: null,
};

export const STATE_PRIORITY: Record<BillingState, number> = {
  active: 0,
  'admin-granted': 1,
  expired: 2,
  none: 3,
  'role-access': 4,
};

function isActiveSubscriptionStatus(status: SubscriptionStatus): boolean {
  return status === 'active' || status === 'trialing';
}

export function resolveBillingState(subscription: Subscription): BillingState {
  if (!isActiveSubscriptionStatus(subscription.status)) return 'expired';
  return subscription.origin === 'stripe' ? 'active' : 'admin-granted';
}

export function timestampToMillis(timestamp: FirestoreTimestamp | Date | null | undefined): number {
  if (!timestamp) return 0;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof (timestamp as { toDate?: () => Date }).toDate === 'function') {
    return (timestamp as { toDate: () => Date }).toDate().getTime();
  }
  return 0;
}

/** Shared by the Settings billing card and the admin subscriptions live view (#7840). */
export function formatBillingAmount(amount: number | null, currency: string | null, interval: BillingInterval | null): string | null {
  if (amount === null || !currency) return null;
  const base = `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  return interval ? `${base} / ${interval}` : base;
}

function timestampToIso(timestamp: FirestoreTimestamp | Date | null | undefined): string | null {
  const millis = timestampToMillis(timestamp);
  return millis > 0 ? new Date(millis).toISOString() : null;
}

/**
 * Picks which subscription to summarise when a user has more than one record
 * (e.g. duplicates left behind by a reconcile). Prefers the best standing
 * (active > admin-granted > expired), then the most recently created.
 */
export function pickPrimarySubscription(subscriptions: Subscription[]): Subscription | null {
  if (subscriptions.length === 0) return null;
  return [...subscriptions].sort((a, b) => {
    const priorityDiff = STATE_PRIORITY[resolveBillingState(a)] - STATE_PRIORITY[resolveBillingState(b)];
    if (priorityDiff !== 0) return priorityDiff;
    return timestampToMillis(b.createdAt) - timestampToMillis(a.createdAt);
  })[0];
}

export function buildBillingSummary(
  subscription: Subscription | null,
  product: Product | null,
  options: { hasRoleGrantedAccess?: boolean } = {}
): BillingSummary {
  if (!subscription) return options.hasRoleGrantedAccess ? ROLE_ACCESS_SUMMARY : NO_SUBSCRIPTION_SUMMARY;

  const state = resolveBillingState(subscription);
  return {
    accessFromRole: options.hasRoleGrantedAccess === true,
    state,
    planName: product?.name ?? null,
    amount: product?.priceAmount ?? null,
    currency: product?.priceCurrency ?? null,
    status: subscription.status,
    currentPeriodEnd: timestampToIso(subscription.currentPeriodEnd),
    isLifetime: subscription.isLifetime,
    origin: subscription.origin,
    canManageInStripe: state === 'active',
    cancelAtPeriodEnd: false,
    interval: null,
  };
}

/**
 * Overlays a live Stripe read onto an already-built summary (PLAN.md constraint 4:
 * `cancel_at_period_end` isn't mirrored into Firestore, so a user who cancels in the
 * portal still reads as `active` until this runs). Callers should fall back to the
 * Firestore-derived summary untouched when the live Stripe read fails.
 *
 * `price` carries the booked price's `unit_amount` / `currency` / `recurring.interval`
 * (the Subscription document records none of these — the product's price is a guess that
 * can be wrong whenever a product has more than one price). Omit it, or pass a null
 * `interval`, to keep the product-derived amount/currency and show no interval rather than
 * inventing one — the caller does this for a missing recurring block, a failed live read,
 * or an admin-granted record with no Stripe price at all.
 */
export function overlayLiveStripeFields(
  summary: BillingSummary,
  live: {
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: number | null;
    price?: { unitAmount: number | null; currency: string | null; interval: BillingInterval | null } | null;
  }
): BillingSummary {
  return {
    ...summary,
    cancelAtPeriodEnd: live.cancelAtPeriodEnd,
    currentPeriodEnd:
      live.currentPeriodEnd != null ? new Date(live.currentPeriodEnd * 1000).toISOString() : summary.currentPeriodEnd,
    amount: live.price ? live.price.unitAmount : summary.amount,
    currency: live.price ? live.price.currency : summary.currency,
    interval: live.price?.interval ?? summary.interval,
  };
}

/**
 * Maps Stripe's subscription status vocabulary onto ours. `incomplete_expired` becomes
 * `canceled` (mirrors `StripeSyncService.syncSubscription`'s existing map — a subscription
 * that never completed its first payment and expired is equivalent to a cancellation here).
 * `unpaid` and `paused` have no exact match; both are display-only once mapped (neither is
 * `active`/`trialing`, so {@link resolveBillingState}-equivalent logic below still reads them
 * as not in good standing) — `past_due` and `incomplete` are the closest existing badges.
 */
export function mapStripeSubscriptionStatus(status: Stripe.Subscription.Status): SubscriptionStatus {
  switch (status) {
    case 'active':
      return 'active';
    case 'trialing':
      return 'trialing';
    case 'past_due':
      return 'past_due';
    case 'incomplete':
      return 'incomplete';
    case 'canceled':
    case 'incomplete_expired':
      return 'canceled';
    case 'unpaid':
      return 'past_due';
    case 'paused':
    default:
      return 'incomplete';
  }
}

export interface LiveStripePrice {
  unitAmount: number | null;
  currency: string | null;
  interval: BillingInterval | null;
}

export interface LiveStripeSubscriptionSnapshot {
  status: Stripe.Subscription.Status;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: number | null;
  price: LiveStripePrice | null;
}

/**
 * Builds a {@link BillingSummary} directly from a live Stripe read (#7839) — unlike
 * {@link buildBillingSummary}, there is no Firestore record underneath to overlay onto:
 * every field comes from `snapshot`/`product` as of this call. `amount`/`currency` prefer
 * the booked price on the snapshot over the product's (possibly different) default price,
 * exactly like {@link overlayLiveStripeFields} already does for the Firestore-backed path.
 */
export function buildLiveBillingSummary(snapshot: LiveStripeSubscriptionSnapshot, product: Product | null): BillingSummary {
  const status = mapStripeSubscriptionStatus(snapshot.status);
  const state: BillingState = isActiveSubscriptionStatus(status) ? 'active' : 'expired';
  return {
    // Ob der Zugang an der Rolle haengt, weiss nur der Aufrufer — die Route setzt es
    // zentral auf dem gewaehlten Summary, damit es nicht an drei Stellen gepflegt wird.
    accessFromRole: false,
    state,
    planName: product?.name ?? null,
    amount: snapshot.price ? snapshot.price.unitAmount : product?.priceAmount ?? null,
    currency: snapshot.price ? snapshot.price.currency : product?.priceCurrency ?? null,
    status,
    currentPeriodEnd: snapshot.currentPeriodEnd != null ? new Date(snapshot.currentPeriodEnd * 1000).toISOString() : null,
    isLifetime: false,
    origin: 'stripe',
    canManageInStripe: state === 'active',
    cancelAtPeriodEnd: snapshot.cancelAtPeriodEnd,
    interval: snapshot.price?.interval ?? null,
  };
}

/**
 * Builds a {@link BillingSummary} for a one-time/lifetime purchase (#7844, ADR-0026) — Stripe
 * has no subscription object for this kind of purchase at all, so there is nothing to read
 * live; the Access grant's own one-time-purchase source (#7845) is the only record of it.
 * Always `active`: a lapsed source would already have been dropped from the grant by the
 * role-validity grace window (ADR-0025) before any caller gets this far.
 */
export function buildOneTimePurchaseBillingSummary(
  params: { expiresAtMillis: number | null; isLifetime: boolean },
  product: Product | null,
): BillingSummary {
  return {
    accessFromRole: false,
    state: 'active',
    planName: product?.name ?? null,
    amount: product?.priceAmount ?? null,
    currency: product?.priceCurrency ?? null,
    status: 'active',
    currentPeriodEnd: params.expiresAtMillis != null ? new Date(params.expiresAtMillis).toISOString() : null,
    isLifetime: params.isLifetime,
    origin: 'stripe',
    canManageInStripe: true,
    cancelAtPeriodEnd: false,
    interval: null,
  };
}

/**
 * Picks which summary to show when a caller has more than one candidate — a live Stripe
 * subscription, an admin-granted record, a lifetime purchase, or several of these at once
 * (#7839 widens {@link pickPrimarySubscription}'s own-source-only selection to candidates
 * built from heterogeneous sources, which no longer share a single `Subscription` shape to
 * sort directly). Same ordering: best standing first (active > admin-granted > expired),
 * then most recently created.
 */
export function pickPrimaryBillingSummary(
  candidates: Array<{ summary: BillingSummary; createdAtMillis: number }>
): BillingSummary | null {
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => {
    const priorityDiff = STATE_PRIORITY[a.summary.state] - STATE_PRIORITY[b.summary.state];
    if (priorityDiff !== 0) return priorityDiff;
    return b.createdAtMillis - a.createdAtMillis;
  })[0].summary;
}
